import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const StatChip = ({ label, value, accent }) => (
  <div className={`flex flex-col items-center border p-2 bg-[rgba(0,0,0,0.1)] flex-1 min-w-0 ${accent ? 'border-[#d8ceb3]' : 'border-[#658d71]'}`}>
    <span className={`text-base font-black tabular-nums ${accent ? 'text-[#d8ceb3]' : 'text-white'}`}>{value ?? '--'}</span>
    <span className="text-[7px] uppercase tracking-widest text-[#658d71] font-bold mt-0.5 text-center leading-tight">{label}</span>
  </div>
);

const ScoringPlay = ({ play, index, awayAbbr, homeAbbr }) => {
  const event = play.result?.event || '--';
  const rbi = play.result?.rbi || 0;
  const inning = play.about?.inning;
  const isTop = play.about?.isTopInning;
  const isHR = event === 'Home Run';
  const desc = play.result?.description || '';
  const awayScore = play.about?.awayScore ?? '--';
  const homeScore = play.about?.homeScore ?? '--';

  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`flex items-start gap-2 p-2 border-b border-[rgba(101,141,113,0.2)] last:border-0 ${isHR ? 'bg-[rgba(196,51,62,0.06)]' : ''}`}
    >
      <div className="shrink-0 flex flex-col items-center w-10">
        <span className="text-[8px] font-bold text-[#658d71] uppercase tracking-widest">
          {isTop ? '▲' : '▼'}{inning}
        </span>
        <span className="text-[7px] font-bold text-[#a9c9b1] uppercase">{isTop ? awayAbbr : homeAbbr}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[10px] font-black uppercase tracking-wide ${isHR ? 'text-[#c4333e]' : 'text-[#d8ceb3]'}`}>
            {isHR ? '💣 ' : ''}{event}
          </span>
          {rbi > 0 && (
            <span className="text-[8px] bg-[#c4333e] text-white px-1 font-black">{rbi} RBI</span>
          )}
          <span className="ml-auto text-[9px] tabular-nums font-black text-[#a9c9b1] shrink-0">
            {awayScore}–{homeScore}
          </span>
        </div>
        <p className="text-[8px] text-[#658d71] leading-tight mt-0.5 line-clamp-2">{desc}</p>
      </div>
    </motion.div>
  );
};

const DecisionCard = ({ label, name, stats, color }) => (
  <div className="flex-1 border border-[#658d71] p-2.5 bg-[rgba(0,0,0,0.1)]" style={{ borderLeftWidth: 3, borderLeftColor: color }}>
    <span className="text-[8px] uppercase tracking-widest font-bold text-[#658d71]">{label}</span>
    <p className="text-[11px] font-black uppercase tracking-wide text-white mt-0.5 truncate">{name || '--'}</p>
    {stats && (
      <div className="flex gap-2 mt-1 flex-wrap">
        {Object.entries(stats).map(([k, v]) => (
          <div key={k} className="flex flex-col items-center">
            <span className="text-[7px] text-[#658d71] uppercase tracking-widest font-bold">{k}</span>
            <span className="text-[9px] font-black text-[#d8ceb3] tabular-nums">{v ?? '--'}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const RecapSummary = ({ boxData, pitches, awayAbbr, homeAbbr, game }) => {
  const allPlays = game?._allPlays || [];
  const decisions = game?.decisions;

  const { scoringPlays, teamStats, highlights } = useMemo(() => {
    const scoring = allPlays.filter(p => (p.result?.rbi > 0) || p.result?.event === 'Home Run').slice(0, 15);

    const away = boxData?.awayTeamStats;
    const home = boxData?.homeTeamStats;
    const awayPit = boxData?.awayPitchers || [];
    const homePit = boxData?.homePitchers || [];

    const totalPitches = pitches.length;
    const strikes = pitches.filter(p => p.isStrike).length;
    const balls = pitches.filter(p => p.isBall).length;
    const inPlay = pitches.filter(p => p.isHit).length;
    const homeRuns = allPlays.filter(p => p.result?.event === 'Home Run').length;

    const topBatterAway = [...(boxData?.away || [])].sort((a, b) => (b.h * 2 + b.hr * 4 + b.rbi) - (a.h * 2 + a.hr * 4 + a.rbi))[0];
    const topBatterHome = [...(boxData?.home || [])].sort((a, b) => (b.h * 2 + b.hr * 4 + b.rbi) - (a.h * 2 + a.hr * 4 + a.rbi))[0];
    const starterAway = awayPit[0];
    const starterHome = homePit[0];

    return {
      scoringPlays: scoring,
      teamStats: { away, home, awayPit, homePit },
      highlights: { totalPitches, strikes, balls, inPlay, homeRuns, topBatterAway, topBatterHome, starterAway, starterHome },
    };
  }, [allPlays, boxData, pitches]);

  const getPitcherStats = (pitcher) => pitcher ? {
    IP: pitcher.ip, K: pitcher.k, ER: pitcher.er, BB: pitcher.bb,
  } : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Score Header */}
      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.1)] p-3 flex items-center justify-between gap-3">
        <div className="flex flex-col items-center flex-1">
          <span className="text-[8px] uppercase tracking-widest text-[#658d71] font-bold">Away</span>
          <span className="text-lg font-black uppercase text-[#a9c9b1]">{awayAbbr}</span>
          <span className="text-4xl font-black tabular-nums text-white">{game?.teams?.away?.score ?? '--'}</span>
        </div>
        <div className="flex flex-col items-center shrink-0">
          <span className="text-[9px] uppercase tracking-widest text-[#658d71] font-black">
            {game?.status?.abstractGameState === 'Final' ? 'FINAL' : 'LIVE'}
          </span>
          <span className="text-[#658d71] text-2xl font-black">–</span>
        </div>
        <div className="flex flex-col items-center flex-1">
          <span className="text-[8px] uppercase tracking-widest text-[#658d71] font-bold">Home</span>
          <span className="text-lg font-black uppercase text-[#c4333e]">{homeAbbr}</span>
          <span className="text-4xl font-black tabular-nums text-white">{game?.teams?.home?.score ?? '--'}</span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="flex gap-1.5 flex-wrap sm:flex-nowrap">
        <StatChip label="Pitches" value={highlights.totalPitches} />
        <StatChip label="Strikes" value={highlights.strikes} />
        <StatChip label="Balls" value={highlights.balls} />
        <StatChip label="In Play" value={highlights.inPlay} />
        <StatChip label="Home Runs" value={highlights.homeRuns} accent />
      </div>

      {/* Team Hitting Comparison */}
      {(teamStats.away || teamStats.home) && (
        <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)] flex justify-between">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#d8ceb3]">Team Hitting</span>
            <div className="flex gap-4 text-[9px] font-black uppercase tracking-widest">
              <span className="text-[#a9c9b1]">{awayAbbr}</span>
              <span className="text-[#c4333e]">{homeAbbr}</span>
            </div>
          </div>
          <div className="p-3 flex flex-col gap-1.5">
            {[
              ['Hits', teamStats.away?.hits, teamStats.home?.hits],
              ['Runs', teamStats.away?.runs, teamStats.home?.runs],
              ['HR', teamStats.away?.homeRuns, teamStats.home?.homeRuns],
              ['BB', teamStats.away?.baseOnBalls, teamStats.home?.baseOnBalls],
              ['K', teamStats.away?.strikeOuts, teamStats.home?.strikeOuts],
              ['LOB', teamStats.away?.leftOnBase, teamStats.home?.leftOnBase],
            ].map(([label, a, h]) => {
              const av = Number(a) || 0; const hv = Number(h) || 0;
              const max = Math.max(av, hv, 1);
              return (
                <div key={label} className="flex items-center gap-2">
                  <div className="flex items-center gap-1 flex-1 justify-end">
                    <span className={`text-[10px] font-black tabular-nums ${av >= hv && av > 0 ? 'text-[#a9c9b1]' : 'text-white'}`}>{a ?? '--'}</span>
                    <div className="h-2 bg-[rgba(0,0,0,0.3)] w-16 overflow-hidden flex justify-end">
                      <div className="h-full bg-[#a9c9b1] opacity-70" style={{ width: `${(av / max) * 100}%` }} />
                    </div>
                  </div>
                  <span className="text-[8px] uppercase tracking-widest text-[#658d71] font-bold w-6 text-center shrink-0">{label}</span>
                  <div className="flex items-center gap-1 flex-1">
                    <div className="h-2 bg-[rgba(0,0,0,0.3)] w-16 overflow-hidden">
                      <div className="h-full bg-[#c4333e] opacity-70" style={{ width: `${(hv / max) * 100}%` }} />
                    </div>
                    <span className={`text-[10px] font-black tabular-nums ${hv > av && hv > 0 ? 'text-[#c4333e]' : 'text-white'}`}>{h ?? '--'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Decisions */}
      {decisions && (
        <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#d8ceb3]">Pitching Decisions</span>
          </div>
          <div className="p-3 flex gap-2 flex-wrap sm:flex-nowrap">
            <DecisionCard label="Win" name={decisions.winner?.fullName} stats={getPitcherStats(highlights.starterAway)} color="#a9c9b1" />
            <DecisionCard label="Loss" name={decisions.loser?.fullName} stats={getPitcherStats(highlights.starterHome)} color="#c4333e" />
            {decisions.save?.fullName && (
              <DecisionCard label="Save" name={decisions.save.fullName} stats={null} color="#d8ceb3" />
            )}
          </div>
        </div>
      )}

      {/* Top Performers */}
      {(highlights.topBatterAway || highlights.topBatterHome) && (
        <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#d8ceb3]">Top Batters</span>
          </div>
          <div className="p-3 grid grid-cols-2 gap-2">
            {[
              { batter: highlights.topBatterAway, abbr: awayAbbr, color: '#a9c9b1' },
              { batter: highlights.topBatterHome, abbr: homeAbbr, color: '#c4333e' },
            ].filter(x => x.batter).map(({ batter, abbr, color }) => (
              <div key={abbr} className="border border-[rgba(101,141,113,0.3)] p-2 bg-[rgba(0,0,0,0.08)]">
                <span className="text-[8px] font-bold uppercase tracking-widest" style={{ color }}>{abbr}</span>
                <p className="text-[11px] font-black uppercase text-white truncate">{batter.name.split(' ').pop()}</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {[['H', batter.h], ['HR', batter.hr], ['RBI', batter.rbi], ['AVG', batter.avg]].map(([k, v]) => (
                    v > 0 || k === 'AVG' ? (
                      <div key={k} className="flex flex-col items-center">
                        <span className="text-[7px] text-[#658d71] uppercase font-bold">{k}</span>
                        <span className="text-[9px] font-black text-[#d8ceb3]">{v}</span>
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scoring Plays */}
      {scoringPlays.length > 0 && (
        <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#d8ceb3]">Scoring Plays</span>
            <span className="ml-2 text-[8px] text-[#658d71] font-bold uppercase tracking-widest">({scoringPlays.length})</span>
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            {scoringPlays.map((play, i) => (
              <ScoringPlay key={i} play={play} index={i} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecapSummary;